import React from 'react';

// ════════════════════════════════════════════════════════════════════════
// AgentIcon — avatar do agente de IA
// ────────────────────────────────────────────────────────────────────────
// Substitui o "A" mono no círculo lime do chat (AgentMsg) e demais pontos
// onde o agente aparece. Variações derivadas da identidade «Runway»
// (pista, luzes de aproximação, marshaller) — grade 24, herda currentColor.
//
// Variações (prop `variant`, padrão 'marca' — opção G escolhida):
//   luzes        A · luzes de aproximação (pontos do "I" do logo)
//   pista        B · pista em perspectiva
//   marshaller   C · sinaleiro com bastões
//   radar        D · varredura + blip
//   spark        E · faísca de comando
//   chevrons     F · chevrons de aproximação
//   marca        G · o "A" sem travessão, como no logo (padrão)
// ════════════════════════════════════════════════════════════════════════

export type AgentIconVariant =
  | 'luzes'
  | 'pista'
  | 'marshaller'
  | 'radar'
  | 'spark'
  | 'chevrons'
  | 'marca';

const VARIANT_PATHS: Record<AgentIconVariant, React.ReactNode> = {
  luzes: (
    <>
      <circle cx="12" cy="5" r="2" fill="currentColor" stroke="none" />
      <circle cx="6.5" cy="5" r="1.1" fill="currentColor" stroke="none" opacity=".35" />
      <circle cx="17.5" cy="5" r="1.1" fill="currentColor" stroke="none" opacity=".35" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" opacity=".75" />
      <circle cx="6.5" cy="12" r="1.1" fill="currentColor" stroke="none" opacity=".28" />
      <circle cx="17.5" cy="12" r="1.1" fill="currentColor" stroke="none" opacity=".28" />
      <circle cx="12" cy="19" r="2" fill="currentColor" stroke="none" opacity=".5" />
      <circle cx="6.5" cy="19" r="1.1" fill="currentColor" stroke="none" opacity=".2" />
      <circle cx="17.5" cy="19" r="1.1" fill="currentColor" stroke="none" opacity=".2" />
    </>
  ),
  pista: (
    <>
      <path d="M9.5 4.5 5 19.5" />
      <path d="M14.5 4.5 19 19.5" />
      <path d="M12 6v2.4" />
      <path d="M12 11v2.6" />
      <path d="M12 16.4V19" />
    </>
  ),
  marshaller: (
    <>
      <circle cx="12" cy="5.4" r="2.2" />
      <path d="M12 9.5v6" />
      <path d="M12 10.5 6.8 7.6" />
      <path d="M12 10.5l5.2-2.9" />
      <circle cx="5.4" cy="6.8" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="18.6" cy="6.8" r="1.3" fill="currentColor" stroke="none" />
      <path d="M12 15.5 9 20.5" />
      <path d="M12 15.5l3 5" />
    </>
  ),
  radar: (
    <>
      <path d="M12 12 19 6.5" />
      <path d="M21 12a9 9 0 1 1-2.6-6.4" opacity=".9" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16.2" cy="14.8" r="1.2" fill="currentColor" stroke="none" opacity=".6" />
      <path d="M12 12a4.5 4.5 0 0 1 4.5 4.5" opacity=".45" />
    </>
  ),
  spark: (
    <>
      <path d="M12 3.5c.7 3.9 2.6 5.8 6.5 6.5-3.9.7-5.8 2.6-6.5 6.5-.7-3.9-2.6-5.8-6.5-6.5 3.9-.7 5.8-2.6 6.5-6.5Z" />
      <circle cx="18.5" cy="18.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="18.8" r=".8" fill="currentColor" stroke="none" opacity=".5" />
    </>
  ),
  chevrons: (
    <>
      <path d="M6 17.5 12 20.5 18 17.5" opacity=".4" />
      <path d="M6 11.5 12 14.5 18 11.5" opacity=".7" />
      <path d="M6 5.5 12 8.5 18 5.5" />
    </>
  ),
  // O "A" da marca usa traço mais grosso (+1) que os demais — ver strokeFor().
  marca: <path d="M5.5 19.5 12 4.5 18.5 19.5" />,
};

// A variação 'marca' pede traço 1 px mais grosso para ecoar o peso do logo.
const strokeFor = (variant: AgentIconVariant, base: number) =>
  variant === 'marca' ? base + 1 : base;

export interface AgentIconProps {
  variant?: AgentIconVariant;
  size?: number;
  strokeWidth?: number;
  color?: string;
  style?: React.CSSProperties;
}

export function AgentIcon({
  variant = 'marca',
  size = 16,
  strokeWidth = 1.6,
  color,
  style,
}: AgentIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeFor(variant, strokeWidth)}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color, display: 'block', flex: '0 0 auto', ...style }}
      aria-hidden="true"
    >
      {VARIANT_PATHS[variant]}
    </svg>
  );
}

// ── AgentAvatar ───────────────────────────────────────────────────────────
// O círculo pronto, nos três tons em que o agente aparece. Drop-in no lugar
// do <div>A</div> atual do AgentMsg:
//   <AgentAvatar size={28} />                      // chat (lime, padrão)
//   <AgentAvatar tone="ink" size={28} />           // sobre superfície clara
//   <AgentAvatar tone="chrome" size={30} />        // barra de status escura

export type AgentAvatarTone = 'lime' | 'ink' | 'chrome';

export interface AgentAvatarProps {
  variant?: AgentIconVariant;
  tone?: AgentAvatarTone;
  size?: number;
  style?: React.CSSProperties;
}

const TONE_STYLES: Record<AgentAvatarTone, React.CSSProperties> = {
  lime:   { background: '#C6F23A', color: '#111111' },
  ink:    { background: '#111111', color: '#C6F23A' },
  chrome: { background: '#161613', color: '#C6F23A', border: '1px solid rgba(198,242,58,0.35)' },
};

export function AgentAvatar({
  variant = 'marca',
  tone = 'lime',
  size = 30,
  style,
}: AgentAvatarProps) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        ...TONE_STYLES[tone],
        ...style,
      }}
      aria-label="Agente"
    >
      <AgentIcon variant={variant} size={Math.round(size * 0.57)} strokeWidth={1.9} />
    </span>
  );
}

export default AgentAvatar;
